import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import api from '../services/api';
import Loading from '../components/Loading';
import PrimaryNav from '../components/PrimaryNav';
import StakePage from './StakePage';

export default function Home() {
  const { user, initialized } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [noticeDismissed, setNoticeDismissed] = useState(false);

  useEffect(() => { setNoticeDismissed(false); }, [location.key]);
  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!initialized || !user) return;
    let mounted = true;

    const loadWallet = async () => {
      try {
        const walletRes = await api.wallet.getWallet();
        if (mounted) setWallet(walletRes.data?.data || walletRes.data || null);
      } catch (err) {
        if (mounted) setError(err.response?.data?.message || 'Unable to load your Home wallet data.');
      }
    };

    Promise.all([
      api.profile.getMe(),
      api.wallet.getWallet(),
      api.notifications.list({ limit: 100 }),
    ]).then(([profileRes, walletRes, notificationsRes]) => {
      if (!mounted) return;
      setProfile(profileRes.data?.data?.profile || null);
      setWallet(walletRes.data?.data || walletRes.data || null);
      setUnreadCount(Number(notificationsRes.data?.unreadCount || 0));
    }).catch((err) => {
      if (!mounted) return;
      setError(err.response?.data?.message || 'Unable to load your Home data.');
    });

    const refreshOnReturn = () => {
      if (document.visibilityState === 'visible') loadWallet();
    };
    window.addEventListener('focus', refreshOnReturn);
    document.addEventListener('visibilitychange', refreshOnReturn);

    return () => {
      mounted = false;
      window.removeEventListener('focus', refreshOnReturn);
      document.removeEventListener('visibilitychange', refreshOnReturn);
    };
  }, [initialized, user]);

  if (!initialized || !user) return <Loading variant="page" label="" />;
  if (error && !profile) {
    return (
      <div className="jw-home-error">
        <p>{error}</p>
        <button type="button" onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const avatar = profile?.avatarUrl || '/avatars/avatar_male_01.png?v=20260927';
  const totalBalance = wallet?.totalBalance;
  const query = new URLSearchParams(location.search);
  const noticeMessage = location.state?.message || location.state?.notice || location.state?.error || query.get('message') || query.get('notice') || (['cancelled', 'refunded'].includes(String(query.get('status') || '').toLowerCase()) ? 'Match cancelled, stake refunded.' : '');

  return (
    <div className="jw-home">
      <header className="jw-home-header">
        <button
          type="button"
          className="jw-home-header__profile"
          aria-label="Open profile"
          onClick={() => navigate('/profile')}
        >
          <img src={avatar} alt="" className="jw-home-header__avatar" />
        </button>

        <button
          type="button"
          className="jw-home-header__wallet"
          aria-label="Wallet — view balance and wallet actions"
          onClick={() => navigate('/wallet')}
        >
          <strong>{totalBalance == null ? '—' : '₦' + Number(totalBalance).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
          <span className="jw-home-header__wallet-plus" aria-hidden="true">+</span>
        </button>

        <button
          type="button"
          className="jw-home-header__notifications"
          aria-label={`Open notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
          onClick={() => navigate('/notifications')}
        >
          <span aria-hidden="true">🔔</span>
          {unreadCount > 0 && <span className="jw-home-header__badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
        </button>
      </header>

      <main className="jw-home-main">
        <section className="jw-home-intro" aria-labelledby="todays-games-title">
          <p className="jw-home-kicker">WHOT</p>
          <h1 id="todays-games-title">Today's Games</h1>
          <p>Choose a player mode and join an available stake.</p>
        </section>

        {noticeMessage && !noticeDismissed && (
          <div role="status" aria-live="polite" className="jw-home-match-notice" style={{ margin: '0 0 16px', padding: '12px 14px', borderRadius: '10px', background: '#12372f', border: '1px solid #00d4aa', color: '#d1fae5', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
            <span>{String(noticeMessage)}</span>
            <button type="button" aria-label="Dismiss message" onClick={() => setNoticeDismissed(true)} style={{ background: 'transparent', border: 0, color: 'inherit', fontSize: '18px', cursor: 'pointer', padding: 0 }}>×</button>
          </div>
        )}
        <StakePage />
      </main>

      <PrimaryNav />
    </div>
  );
}
